import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { Search, Download, Printer, RefreshCw, ArrowLeft, ArrowUp, ArrowDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import pdfMake from 'pdfmake/build/pdfmake';
import { vfs } from '../../fonts/vfs_fonts';
import { TDocumentDefinitions } from 'pdfmake/interfaces';

interface Participant {
  student_id: string;
  first_name: string;
  last_name: string;
  first_name_tamil?: string;
  last_name_tamil?: string;
  reference_number?: string;
  house?: {
    house_name: string;
    house_color: string;
  };
  department_id?: string;
  department?: {
    department_id: string;
    name: string;
    name_tamil?: string;
  };
}

interface Department {
  department_id: string;
  name: string;
  name_tamil?: string;
}

type SortDirection = 'asc' | 'desc';

interface SortConfig {
  key: string;
  direction: SortDirection;
}

// Initialize pdfmake with virtual font files
pdfMake.vfs = vfs;

// Configure fonts for pdfmake
pdfMake.fonts = {
  NotoSansTamil: {
    normal: 'NotoSansTamil-Regular.ttf',
    bold: 'NotoSansTamil-Bold.ttf',
    italics: 'NotoSansTamil-Regular.ttf',
    bolditalics: 'NotoSansTamil-Bold.ttf'
  },
  Roboto: {
    normal: 'Roboto-Regular.ttf',
    bold: 'Roboto-Bold.ttf',
    italics: 'Roboto-Regular.ttf',
    bolditalics: 'Roboto-Bold.ttf'
  }
};

// Helper function to get contrasting text color based on background
const getContrastColor = (hexColor: string): string => {
  // Remove the hash if it exists
  hexColor = hexColor.replace('#', '');
  
  // Parse the color
  const r = parseInt(hexColor.substr(0, 2), 16);
  const g = parseInt(hexColor.substr(2, 2), 16);
  const b = parseInt(hexColor.substr(4, 2), 16);
  
  // Calculate luminance
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  
  // Return black or white based on luminance
  return luminance > 0.5 ? '#000000' : '#FFFFFF';
};

const ParticipantCardGenerator: React.FC = () => {
  const { t, i18n } = useTranslation('common');
  const navigate = useNavigate();
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [filteredParticipants, setFilteredParticipants] = useState<Participant[]>([]);
  const [selectedParticipants, setSelectedParticipants] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewParticipant, setPreviewParticipant] = useState<Participant | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [selectedDepartment, setSelectedDepartment] = useState<string>('All');

  useEffect(() => {
    fetchParticipants();
    fetchDepartments();
  }, []);

  useEffect(() => {
    filterParticipants();
  }, [searchTerm, participants, selectedDepartment]);

  const fetchDepartments = async () => {
    try {
      const { data, error } = await supabase
        .from('departments')
        .select('department_id, name, name_tamil')
        .order('name');
      
      if (error) throw error;
      
      setDepartments(data || []);
    } catch (error: any) {
      console.error('Error fetching departments:', error);
    }
  };

  const fetchParticipants = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const { data, error } = await supabase
        .from('students')
        .select(`
          student_id,
          first_name,
          last_name,
          first_name_tamil,
          last_name_tamil,
          reference_number,
          department_id,
          department:departments(
            department_id,
            name,
            name_tamil
          ),
          house:houses(
            house_name,
            house_color
          )
        `)
        .order('last_name');
      
      if (error) throw error;
      
      setParticipants(data || []);
      setFilteredParticipants(data || []);
    } catch (error: any) {
      console.error('Error fetching participants:', error);
      setError(error.message || 'Failed to load participants');
    } finally {
      setLoading(false);
    }
  };

  const filterParticipants = () => {
    let filtered = participants;
    
    // Apply department filter
    if (selectedDepartment !== 'All') {
      filtered = filtered.filter(p => p.department_id === selectedDepartment);
    }
    
    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(p => 
        `${p.first_name} ${p.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
        `${p.first_name_tamil || ''} ${p.last_name_tamil || ''}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (p.reference_number || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (p.house?.house_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (p.department?.name || '').toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Apply sorting if configured
    if (sortConfig) {
      filtered = [...filtered].sort((a, b) => {
        // Handle nested properties with dot notation
        const getNestedProperty = (obj: any, path: string) => {
          return path.split('.').reduce((prev, curr) => {
            return prev ? prev[curr] : null;
          }, obj);
        };
        
        let aValue = getNestedProperty(a, sortConfig.key);
        let bValue = getNestedProperty(b, sortConfig.key);
        
        // Handle null/undefined values
        if (aValue === null || aValue === undefined) return 1;
        if (bValue === null || bValue === undefined) return -1;
        
        // String comparison
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return sortConfig.direction === 'asc' 
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        }
        
        // Number comparison
        return sortConfig.direction === 'asc' 
          ? (aValue > bValue ? 1 : -1)
          : (bValue > aValue ? 1 : -1);
      });
    }
    
    setFilteredParticipants(filtered);
  };

  const handleSelectAll = () => {
    if (selectedParticipants.length === filteredParticipants.length) {
      setSelectedParticipants([]);
    } else {
      setSelectedParticipants(filteredParticipants.map(p => p.student_id));
    }
  };

  const handleSelectParticipant = (id: string) => {
    if (selectedParticipants.includes(id)) {
      setSelectedParticipants(prev => prev.filter(p => p !== id));
    } else {
      setSelectedParticipants(prev => [...prev, id]);
    }
  };

  const handlePreview = (participant: Participant) => {
    setPreviewParticipant(participant);
    setShowPreview(true);
  };

  const requestSort = (key: string) => {
    let direction: SortDirection = 'asc';
    
    if (sortConfig && sortConfig.key === key) {
      direction = sortConfig.direction === 'asc' ? 'desc' : 'asc';
    }
    
    setSortConfig({ key, direction });
  };

  const getSortIcon = (columnKey: string) => {
    if (!sortConfig || sortConfig.key !== columnKey) {
      return null;
    }
    
    return sortConfig.direction === 'asc' 
      ? <ArrowUp className="h-4 w-4 inline-block ml-1" /> 
      : <ArrowDown className="h-4 w-4 inline-block ml-1" />;
  };

  const generatePDF = async () => {
    try {
      setGenerating(true);
      
      // Filter selected participants
      const selected = participants.filter(p => selectedParticipants.includes(p.student_id));
      
      if (selected.length === 0) {
        setError(t('participants.cards.selectAtLeastOne'));
        return;
      }
      
      // Create PDF content for each participant
      const content: any[] = [];
      
      selected.forEach((participant, index) => {
        // Add page break after each participant except the first one
        if (index > 0) {
          content.push({ text: '', pageBreak: 'before' });
        }
        
        // Get house color or default to blue
        const houseColor = participant.house?.house_color || '#2563eb';
        const textColor = getContrastColor(houseColor);
        
        // School name at the top
        content.push({
          text: t('participants.cards.schoolName'),
          font: 'NotoSansTamil',
          fontSize: 16,
          bold: true,
          alignment: 'center',
          margin: [0, 0, 0, 5] // Reduced top margin
        });
        
        // Reference number with house color background
        content.push({
          stack: [
            {
              canvas: [
                {
                  type: 'rect',
                  x: 0,
                  y: 0,
                  w: 515, // A5 landscape width - margins
                  h: 140, // Increased height for larger text
                  r: 5,
                  color: houseColor
                }
              ]
            },
            {
              text: participant.reference_number || t('participants.cards.noReference'),
              fontSize: 125, // Increased font size to 125px
              bold: true,
              color: textColor,
              alignment: 'center',
              margin: [0, -150, 0, 30] // Adjusted margin to center text vertically
            }
          ],
          margin: [0, 0, 0, 15]
        });
        
        // Name (Tamil or English)
        if (participant.first_name_tamil && participant.last_name_tamil) {
          content.push({
            text: `${participant.first_name_tamil} ${participant.last_name_tamil}`,
            font: 'NotoSansTamil',
            fontSize: 30, // Increased font size for Tamil name
            bold: true,
            alignment: 'center',
            margin: [0, 5, 0, 10]
          });
        } else {
          content.push({
            text: `${participant.first_name} ${participant.last_name}`,
            fontSize: 30, // Increased font size for consistency
            bold: true,
            alignment: 'center',
            margin: [0, 5, 0, 10]
          });
        }
             
        // House color indicator
        content.push({
          canvas: [
            {
              type: 'rect',
              x: 207.5, // Centered: (515 - 100) / 2
              y: 0,
              w: 100,
              h: 10,
              color: participant.house?.house_color || '#2563eb'
            }
          ],
          margin: [0, 0, 0, 0]
        });
      });
      
      // Define the document
      const docDefinition: TDocumentDefinitions = {
        pageSize: 'A5',
        pageOrientation: 'landscape',
        pageMargins: [40, 40, 40, 40],
        content: content,
        defaultStyle: {
          font: 'Roboto'
        }
      };
      
      // Generate and download the PDF
      pdfMake.createPdf(docDefinition).download('participant_cards.pdf');
      
    } catch (error: any) {
      console.error('Error generating PDF:', error);
      setError(error.message || 'Failed to generate PDF');
    } finally {
      setGenerating(false);
    }
  };

  const printSingleCard = async (participant: Participant) => {
    try {
      setGenerating(true);
      
      // Get house color or default to blue
      const houseColor = participant.house?.house_color || '#2563eb';
      const textColor = getContrastColor(houseColor);
      
      // Create PDF content for the participant
      const content: any[] = [];
      
      // School name at the top
      content.push({
        text: t('participants.cards.schoolName'),
        font: 'NotoSansTamil',
        fontSize: 16,
        bold: true,
        alignment: 'center',
        margin: [0, 0, 0, 5] // Reduced top margin
      });
      
      // Reference number with house color background
      content.push({
        stack: [
          {
            canvas: [
              {
                type: 'rect',
                x: 0,
                y: 0,
                w: 515, // A5 landscape width - margins
                h: 140, // Increased height for larger text
                r: 5,
                color: houseColor
              }
            ]
          },
          {
            text: participant.reference_number || t('participants.cards.noReference'),
            fontSize: 125, // Increased font size to 125px
            bold: true,
            color: textColor,
            alignment: 'center',
            margin: [0, -120, 0, 30] // Adjusted margin to center text vertically
          }
        ],
        margin: [0, 0, 0, 15]
      });
      
      // Name (Tamil or English)
      if (participant.first_name_tamil && participant.last_name_tamil) {
        content.push({
          text: `${participant.first_name_tamil} ${participant.last_name_tamil}`,
          font: 'NotoSansTamil',
          fontSize: 30, // Increased font size for Tamil name
          bold: true,
          alignment: 'center',
          margin: [0, 5, 0, 10]
        });
      } else {
        content.push({
          text: `${participant.first_name} ${participant.last_name}`,
          fontSize: 30, // Increased font size for consistency
          bold: true,
          alignment: 'center',
          margin: [0, 5, 0, 10]
        });
      }
      
      // House color indicator
      content.push({
        canvas: [
          {
            type: 'rect',
            x: 207.5, // Centered: (515 - 100) / 2
            y: 0,
            w: 100,
            h: 10,
            color: participant.house?.house_color || '#2563eb'
          }
        ],
        margin: [0, 0, 0, 0]
      });
      
      // Define the document
      const docDefinition: TDocumentDefinitions = {
        pageSize: 'A5',
        pageOrientation: 'landscape',
        pageMargins: [40, 40, 40, 40],
        content: content,
        defaultStyle: {
          font: 'Roboto'
        }
      };
      
      // Generate and open the PDF for printing
      pdfMake.createPdf(docDefinition).print();
      
    } catch (error: any) {
      console.error('Error printing card:', error);
      setError(error.message || 'Failed to print card');
    } finally {
      setGenerating(false);
    }
  };

  // Language selector component
  const LanguageSelector = () => (
    <div className="flex space-x-2">
      <button
        onClick={() => i18n.changeLanguage('en')}
        className={`px-2 py-1 rounded text-sm ${i18n.language === 'en' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}
      >
        EN
      </button>
      <button
        onClick={() => i18n.changeLanguage('nn')}
        className={`px-2 py-1 rounded text-sm ${i18n.language === 'nn' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}
      >
        NN
      </button>
      <button
        onClick={() => i18n.changeLanguage('ta')}
        className={`px-2 py-1 rounded text-sm ${i18n.language === 'ta' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}
      >
        TA
      </button>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <button
            onClick={() => navigate(-1)}
            className="mr-4 inline-flex items-center p-2 border border-transparent rounded-full shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <ArrowLeft className="h-5 w-5" aria-hidden="true" />
          </button>
          <h1 className="text-2xl font-bold text-gray-900">{t('participants.cards.title')}</h1>
        </div>
        <div className="flex space-x-3">
          <LanguageSelector />
          <button
            onClick={fetchParticipants}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            {t('participants.cards.refresh')}
          </button>
          <button
            onClick={generatePDF}
            disabled={selectedParticipants.length === 0 || generating}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-300"
          >
            <Download className="h-4 w-4 mr-2" />
            {generating ? t('participants.cards.generating') : t('participants.cards.generatePDF')}
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4">
          <div className="flex">
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 border-b border-gray-200 sm:px-6">
          <div className="flex flex-col md:flex-row space-y-3 md:space-y-0 md:space-x-4 md:items-center">
            <div className="flex-1 relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={t('participants.cards.searchPlaceholder')}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>
            <div className="flex-shrink-0">
              <select
                value={selectedDepartment}
                onChange={(e) => setSelectedDepartment(e.target.value)}
                className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
              >
                <option value="All">{t('participants.filters.allDepartments')}</option>
                {departments.map(dept => (
                  <option key={dept.department_id} value={dept.department_id}>
                    {dept.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex-shrink-0">
              <button
                onClick={handleSelectAll}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                {selectedParticipants.length === filteredParticipants.length && filteredParticipants.length > 0 
                  ? t('participants.cards.deselectAll') 
                  : t('participants.cards.selectAll')}
              </button>
            </div>
          </div>
        </div>
        
        {loading ? (
          <div className="flex justify-center items-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : filteredParticipants.length === 0 ? (
          <div className="text-center py-12">
            <div className="mx-auto h-12 w-12 text-gray-400">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              {t('participants.cards.noParticipantsFound')}
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              {t('participants.cards.adjustSearch')}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <input
                      type="checkbox"
                      checked={selectedParticipants.length === filteredParticipants.length && filteredParticipants.length > 0}
                      onChange={handleSelectAll}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                  </th>
                  <th 
                    scope="col" 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => requestSort('reference_number')}
                  >
                    {t('participants.cards.referenceNumber')}
                    {getSortIcon('reference_number')}
                  </th>
                  <th 
                    scope="col" 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => requestSort('first_name')}
                  >
                    {t('participants.cards.name')}
                    {getSortIcon('first_name')}
                  </th>
                  <th 
                    scope="col" 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => requestSort('first_name_tamil')}
                  >
                    {t('participants.cards.tamilName')}
                    {getSortIcon('first_name_tamil')}
                  </th>
                  <th 
                    scope="col" 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => requestSort('house.house_name')}
                  >
                    {t('participants.cards.house')}
                    {getSortIcon('house.house_name')}
                  </th>
                  <th 
                    scope="col" 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => requestSort('department.name')}
                  >
                    {t('participants.table.department')}
                    {getSortIcon('department.name')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('participants.cards.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredParticipants.map((participant) => (
                  <tr key={participant.student_id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={selectedParticipants.includes(participant.student_id)}
                        onChange={() => handleSelectParticipant(participant.student_id)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-mono text-gray-900">
                        {participant.reference_number || t('participants.cards.noReference')}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {participant.first_name} {participant.last_name}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {participant.first_name_tamil} {participant.last_name_tamil}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {participant.house && (
                          <>
                            <div 
                              className="h-3 w-3 rounded-full mr-2" 
                              style={{ backgroundColor: participant.house.house_color }}
                            />
                            <div className="text-sm text-gray-900">
                              {participant.house.house_name}
                            </div>
                          </>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {participant.department?.name || '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => handlePreview(participant)}
                        className="text-blue-600 hover:text-blue-900 mr-3"
                      >
                        {t('participants.cards.preview')}
                      </button>
                      <button
                        onClick={() => printSingleCard(participant)}
                        className="text-green-600 hover:text-green-900"
                        disabled={generating}
                      >
                        <Printer className="h-5 w-5 inline-block" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Preview Modal */}
      {showPreview && previewParticipant && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl max-w-2xl w-full">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">{t('participants.cards.previewTitle')}</h2>
              <button
                onClick={() => setShowPreview(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div 
              className="card border-2 border-gray-300 rounded-lg p-4 flex flex-col items-center"
              style={{ width: '100%', aspectRatio: '210/148' }}
            >
              <div className="text-center text-sm mb-1">
                <span style={{ fontFamily: 'Noto Sans Tamil, Arial, sans-serif' }}>{t('participants.cards.schoolName')}</span>
              </div>
              
              <div 
                className="w-full rounded-lg flex justify-center items-center mb-3"
                style={{ 
                  backgroundColor: previewParticipant.house?.house_color || '#2563eb',
                  color: getContrastColor(previewParticipant.house?.house_color || '#2563eb'),
                  height: '120px'
                }}
              >
                <div className="text-8xl font-bold">
                  {previewParticipant.reference_number || t('participants.cards.noReference')}
                </div>
              </div>
              
              {previewParticipant.first_name_tamil && previewParticipant.last_name_tamil ? (
                <div className="text-3xl mb-3 text-center" style={{ fontFamily: 'Noto Sans Tamil, Arial, sans-serif' }}>
                  {previewParticipant.first_name_tamil} {previewParticipant.last_name_tamil}
                </div>
              ) : (
                <div className="text-3xl font-semibold mb-3 text-center">
                  {previewParticipant.first_name} {previewParticipant.last_name}
                </div>
              )}
              
              <div 
                className="w-24 h-3 mx-auto" 
                style={{ backgroundColor: previewParticipant.house?.house_color || '#2563eb' }}
              />
            </div>
            
            <div className="flex justify-end mt-6 space-x-3">
              <button
                onClick={() => printSingleCard(previewParticipant)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                disabled={generating}
              >
                <Printer className="h-4 w-4 mr-2" />
                {t('participants.cards.print')}
              </button>
              <button
                onClick={() => {
                  setSelectedParticipants([previewParticipant.student_id]);
                  generatePDF();
                }}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                disabled={generating}
              >
                <Download className="h-4 w-4 mr-2" />
                {generating ? t('participants.cards.generating') : t('participants.cards.downloadPDF')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ParticipantCardGenerator;